import { useState } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";
import { PageContainer } from "../../../../components/common/page-container";
import { AppCard } from "../../../../components/common/card";
import { PageContentSkeleton } from "../../../../components/common/page-content-skeleton";
import { Text, Heading } from "../../../../components/common/typography";
import { Breadcrumbs } from "../../../../components/common/breadcrumbs";
import { Button } from "../../../../components/common/button/button";
import { Dialog } from "../../../../components/common/dialog";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { useChangeRequestDetails } from "../../hooks/useChangeRequestDetails";
import {
  useCancelChangeRequest,
  useSubmitChangeRequest,
} from "../../hooks/useChangeRequestMutations";
import { changeRequestsService } from "../../../api_client";
import { ChangeRequestStatusBadge } from "../components/change-request-status-badge";
import { ChangeRequestDetailView } from "../components/change-request-detail-view";
import { EditableProposedChanges } from "../components/editable-proposed-changes";
import { DraftChangeRequestActions } from "../components/draft-change-request-actions";
import { ChangesRequestedBanner } from "../components/changes-requested-banner";
import { humanizeTypeForOwner } from "../../data/change-request-type";
import { isCancellable } from "../../data/change-request-status";
import { getChangeRequestsListPath } from "../../data/change-request-list-path";
import { useMePartner } from "../../../../swr";
import {
  ChangeRequestDtoStatusEnum,
  ChangeRequestDtoTypeEnum,
} from "../../../../api";
import { useProductVariants } from "../../../products/hooks/useProduct";
import { useProductAttributeLines } from "../../../products/hooks/useProductAttributeLines";
import { useCountries } from "../../../products/hooks/useCountries";
import { useCategories } from "../../../products/hooks/useCategories";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";
import type {
  ProductContext,
  VariantLite,
} from "../components/proposed-changes-diff";
import { useMemo } from "react";

// Phones: PageContainer has no padding below `sm` — keep the 16px side gutters.
export function ChangeRequestDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const { isBuyer, isSeller } = useMePartner();
  const listPath = getChangeRequestsListPath({ isBuyer, isSeller });
  const { data: request, isLoading, error } = useChangeRequestDetails(id);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const { submitHandler: cancelHandler, isLoading: isCancelling } =
    useCancelChangeRequest(id ?? "");
  const { submitHandler: resubmitHandler, isLoading: isResubmitting } =
    useSubmitChangeRequest(id ?? "");

  // Product context (variants, country/category names) so the inline editor
  // and the read-only diff can render meaningful labels. Only meaningful for
  // PRODUCT_UPDATE requests; harmless otherwise.
  const { data: productData } = useProductVariants(request?.targetProductId ?? 0);
  const { data: attributeLines } = useProductAttributeLines(request?.targetProductId);
  const { data: countries } = useCountries();
  const { data: categories } = useCategories();

  const productContext = useMemo<ProductContext | undefined>(() => {
    if (!request || request.targetProductId == null) return undefined;
    const variantsById = new Map<number, VariantLite>();
    for (const v of productData?.variants ?? []) {
      variantsById.set(v.id, {
        id: v.id,
        sku: v.defaultCode ?? null,
        displayName: v.displayName ?? v.name ?? `Variant #${v.id}`,
        currentPrice: v.sellerPrice ?? null,
        currentStock: v.supplierStock ?? null,
        currentDiscount: v.discountPercentage ?? null,
      });
    }
    const countryNameById = new Map<number, string>();
    for (const c of countries ?? []) countryNameById.set(c.id, c.name);
    const categoryNameById = new Map<number, string>();
    for (const c of categories ?? []) {
      categoryNameById.set(c.id, c.completeName ?? c.displayName ?? c.name);
    }
    const attributeValueNameById = new Map<number, string>();
    for (const line of attributeLines ?? []) {
      for (const val of line.values ?? []) attributeValueNameById.set(val.id, val.name);
    }
    return {
      templateName: productData?.product?.displayName ?? productData?.product?.name,
      variantsById,
      countryNameById,
      categoryNameById,
      attributeValueNameById,
      galleryImageIds: productData?.product?.galleryImageIds ?? [],
    };
  }, [request, productData, attributeLines, countries, categories]);

  if (isLoading) {
    return (
      <PageContainer>
        <PageContentSkeleton cardCount={2} actionCount={1} />
      </PageContainer>
    );
  }

  if (error || !request) {
    return (
      <PageContainer>
        <AppCard title="Error" color="bg-bgDefault" className="w-full">
          <Text variant="body" className="text-accentError">
            Failed to load change request. Please try again later.
          </Text>
        </AppCard>
      </PageContainer>
    );
  }

  async function handleEditSubmit(next: Record<string, unknown>) {
    if (!id) return;
    setIsSavingEdit(true);
    try {
      await changeRequestsService.update(id, { proposedChanges: next });
      await changeRequestsService.submit(id);
      showSuccessToast("Change request resubmitted for admin review.");
      await mutate(
        (key) =>
          typeof key === "string" &&
          (key.startsWith("my-change-requests") ||
            key === `change-request-details-${id}`),
      );
      setIsEditing(false);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string | string[] } } })
          ?.response?.data?.message;
      const flat = Array.isArray(msg) ? msg.join(", ") : msg;
      showErrorToast(flat || "Failed to resubmit change request.");
    } finally {
      setIsSavingEdit(false);
    }
  }

  const canEdit =
    request.status === ChangeRequestDtoStatusEnum.ChangesRequested &&
    request.type === ChangeRequestDtoTypeEnum.ProductUpdate;

  return (
    <PageContainer>
      <div className="flex flex-col gap-6 w-full min-w-0">
        <Breadcrumbs
          items={[
            { label: "Home", path: AppRoutes.INDEX },
            { label: "My Change Requests", path: listPath },
            { label: humanizeTypeForOwner(request.type) },
          ]}
        />

        <div className="flex items-center gap-4 w-full min-w-0 max-md:flex-wrap max-md:gap-3">
          <div className="flex-1 flex items-center gap-4 min-w-0 max-md:basis-full max-md:flex-wrap max-md:gap-2">
            <Heading variant="h2" tone="dark" className="truncate max-md:whitespace-normal">
              {humanizeTypeForOwner(request.type)}
            </Heading>
            <ChangeRequestStatusBadge status={request.status} />
          </div>
          {!isEditing &&
            request.status === ChangeRequestDtoStatusEnum.ChangesRequested && (
              <>
                {canEdit && (
                  <Button
                    variant="primary"
                    disabled={isResubmitting || isCancelling}
                    onClick={() => setIsEditing(true)}
                  >
                    Edit & resubmit
                  </Button>
                )}
                <Button
                  variant="secondary"
                  disabled={isResubmitting || isCancelling}
                  onClick={() => void resubmitHandler()}
                >
                  {isResubmitting ? "Resubmitting…" : "Resubmit"}
                </Button>
              </>
            )}
          {!isEditing && (
            <DraftChangeRequestActions request={request} disabled={isCancelling} />
          )}
          {!isEditing && isCancellable(request.status) && (
            <Button
              variant="secondary"
              disabled={isCancelling || isResubmitting}
              onClick={() => setCancelOpen(true)}
            >
              Cancel request
            </Button>
          )}
        </div>

        <Dialog
          open={cancelOpen}
          setOpen={setCancelOpen}
          title="Cancel this change request?"
          text="The request will be marked as cancelled and won't be reviewed. You can create a new one later."
          buttonText={isCancelling ? "Cancelling..." : "Yes, cancel it"}
          cancelText="Keep it"
          busy={isCancelling}
          onConfirm={async () => {
            await cancelHandler();
            setCancelOpen(false);
          }}
        />

        {isEditing ? (
          <>
            <ChangesRequestedBanner
              reviewNote={request.reviewNote}
              reviewedAt={request.reviewedAt}
            />
            <EditableProposedChanges
              proposedChanges={
                (request.proposedChanges ?? {}) as Record<string, unknown>
              }
              productContext={productContext}
              isSubmitting={isSavingEdit}
              onCancel={() => setIsEditing(false)}
              onSubmit={handleEditSubmit}
            />
          </>
        ) : (
          <ChangeRequestDetailView request={request} />
        )}
      </div>
    </PageContainer>
  );
}
